import React from 'react'
import clsx from 'clsx'
import { Fade, Menu, MenuItem } from '@mui/material'

import BaseModal from '../../../ChatModals/BaseModal2'
import Button from '../../../../atoms/Button2'
import ButtonSecondary from '../../../../atoms/ButtonSecondary'
import MediaAvatar from '../../../../atoms/MediaAvatar'
import RemoveAgentIcon from './Icon'
import useDelayedUserSearch from '../../../../../hooks/useDelayedUserSearch/v2'
import Result from '../../../../organisms/ChatWindowV2/Header/EditParticipantsModal/Result'

import { useBreakpoint } from '../../../../../providers/BreakpointProvider'
import { useUserAPI } from '../../../../../providers/UserAPIProvider'
import { uniqueArray } from '../../../../../utilities'

import useStyles from './styles'

const BACKSPACE = 'Backspace'

interface AddAsigneeModalProps {
  onClose: () => void
  allUsers: any
  open: boolean
  addAsignee: (agents: Array<Number>) => void
}

const AddAsigneeModal: React.FC<AddAsigneeModalProps> = ({
  onClose,
  open,
  allUsers,
  addAsignee
}) => {
  const { isMedium } = useBreakpoint()
  const { search, results, noResults, setNoResults } =
    useDelayedUserSearch(false)
  const { updateUsers } = useUserAPI()
  const [agents, setAgents] = React.useState([])
  const [searchVal, setSearchVal] = React.useState('')
  const [menuOpen, setMenuOpen] = React.useState(false)

  const inputRef = React.useRef(null)
  const anchorRef = React.useRef<HTMLDivElement>()

  React.useEffect(() => {
    if (results.length > 0) {
      updateUsers(results)
    }
  }, [updateUsers, results])

  if (searchVal.length <= 2 && noResults) {
    setNoResults(false)
  }

  const onSearchChange = React.useCallback(
    value => {
      setSearchVal(value)
      search(value)
    },
    [search]
  )

  const handleAssignClick = React.useCallback(() => {
    addAsignee(agents)
    onClose()
  }, [addAsignee, onClose, agents])

  const handleOnKeyDown = React.useCallback(
    e => {
      if (
        e.key === BACKSPACE &&
        e.target.value.length === 0 &&
        agents.length > 0
      ) {
        e.preventDefault()

        // Remove last id from list
        setAgents(old => old.slice(0, -1))
      }
    },
    [agents]
  )

  const removeAgent = React.useCallback(id => {
    setAgents(old => [...old].filter(i => i !== id))
  }, [])

  const showResults = React.useMemo(
    () => (results.length > 0 || noResults) && searchVal.length > 2,
    [noResults, results.length, searchVal.length]
  )

  React.useEffect(() => {
    setMenuOpen(showResults)
    if (showResults) {
      // Refocus input
      if (inputRef.current) {
        setTimeout(() => {
          inputRef.current.focus()
        }, 0)
      }
    }
  }, [showResults])

  const classes = useStyles()

  return (
    <React.Fragment>
      <BaseModal
        open={open}
        title='Search Team'
        onClose={onClose}
        hideTitle={false}
        PaperProps2={{
          style: {
            padding: '30px 16px 30px 16px',
            border: '1px solid #C6C6C6',
            borderRadius: '4px',
            width: isMedium ? 'calc(100% - 4rem)' : '100%',
            maxWidth: isMedium ? 'unset' : '35%'
          }
        }}
        classes={{ title: classes.modalTitle, close: classes.modalClose }}
      >
        <div className={classes.root}>
          <div className={classes.groupContainer}>
            <div className={classes.inputArea} ref={anchorRef}>
              {agents.map(id => {
                const user = allUsers[id]
                return (
                  <div className={classes.chip} key={id}>
                    <MediaAvatar
                      hideCard={false}
                      className={classes.chipAvatar}
                      user={user}
                    />
                    <label>{user?.name || 'loading...'}</label>
                    <RemoveAgentIcon
                      onClick={() => {
                        removeAgent(id)
                      }}
                      className={classes.chipAction}
                    />
                  </div>
                )
              })}

              <input
                ref={inputRef}
                value={searchVal}
                className={classes.agentSearchInput}
                placeholder='Search agent and teammates'
                onChange={e => {
                  const text = e.target.value
                  onSearchChange(text)
                }}
                onKeyDown={handleOnKeyDown}
              />
            </div>
            <Menu
              open={menuOpen}
              disableEnforceFocus
              anchorEl={anchorRef.current}
              anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
              transformOrigin={{ horizontal: 'center', vertical: 4 }}
              TransitionComponent={Fade}
              classes={{ paper: classes.resultsContainer }}
              PaperProps={{
                style: {
                  width: `${
                    anchorRef.current?.getBoundingClientRect()?.width ?? 0
                  }px`
                }
              }}
            >
              <label className={classes.ReAssignLabel}>Re-Assign</label>

              {noResults && <div className={classes.noResult}>No results</div>}
              <div className={classes.results}>
                {results.map(user => (
                  <MenuItem
                    key={user.id}
                    component='div'
                    onClick={() => {
                      setAgents(old => uniqueArray(old, user.id))
                      // Reset state
                      setSearchVal('')
                      setMenuOpen(false)
                      search('')
                    }}
                    className={classes.result}
                    disableGutters
                  >
                    <Result user={user} />
                  </MenuItem>
                ))}
              </div>
            </Menu>
          </div>
        </div>
        <div className={classes.buttonSection}>
          <ButtonSecondary className={classes.button} onClick={onClose}>
            Cancel
          </ButtonSecondary>
          <Button
            onClick={handleAssignClick}
            className={clsx(classes.button)}
            disabled={agents.length === 0}
          >
            Assign
          </Button>
        </div>
      </BaseModal>
    </React.Fragment>
  )
}

export default AddAsigneeModal
